import { MultiValue, SingleValue } from "react-select";
import ActivitySector from "./ActivitySector";
import Company from "./Company";
import CompanyCourse from "./CompanyCourse";
import { IFilters } from "./Fetch";
import User from "./IUser";
import Opco from "./Opco";
import { ISelectOption } from "./Select";
import CompanyStatus from "./CompanyStatus";
import MontageMeetingStatus from "./MontageMeetingStatus";

export type MeetingType = "face-to-face" | "phone" | "video";

export const COMPANY_MEETING_TYPE: ISelectOption<MeetingType>[] = [
  {
    value: "face-to-face",
    label: "Présentiel",
  },
  {
    value: "phone",
    label: "Téléphonique",
  },
  {
    value: "video",
    label: "Visio",
  },
];

export type CalendarType = "commercial" | "montage";

export const CALENDAR_TYPE_OPTIONS: ISelectOption<CalendarType>[] = [
  { value: "commercial", label: "Commercial" },
  { value: "montage", label: "Montage" },
];

interface Meeting {
  id: string;
  addressId: string | null;
  companyId: string | null;
  link: string | null;
  /** Rendez-vous sans entreprise seulement (spec 2026-10-08) : le titre remplace la fiche. */
  title: string | null;
  type: MeetingType;
  calendarType: CalendarType;
  authorId: string | null;
  author: User | null;
  csmOwnerId: string | null;
  csmOwner: User | null;
  /** Calculé par l'API : le client ne connaît pas les liaisons commerciales de
   *  l'entreprise et ne peut donc pas rejouer cette règle sans en diverger. */
  canManage?: boolean;
  montageStatusId: string | null;
  montageStatus: MontageMeetingStatus | null;
  restricted?: false;
  /** Posé par l'API sur un rendez-vous ouvert par « Visibilité calendrier » :
   *  il n'arrive alors qu'avec `startAt` et `ownerName`, rien d'autre. */
  masked?: boolean;
  /** Le commercial de la fiche. Seule information transmise si `masked`. */
  ownerName?: string | null;
  startAt: string;
  endAt: string;
  durationMinutes?: number;
  company: Company | null;
  companyCourses: CompanyCourse[];
  createdAt: string;
  updatedAt: string;
}

export interface MeetingForm {
  placeId?: string;
  link: string | null;
  /** Choisie dans la modale ouverte depuis un calendrier ; facultative. */
  company?: Company | null;
  /** Obligatoire quand aucune entreprise n'est choisie. */
  title?: string;
  type: ISelectOption<MeetingType>;
  calendarType?: ISelectOption<CalendarType>;
  status: SingleValue<CompanyStatus>;
  companyCourses?: MultiValue<CompanyCourse>;
  startAt: string;
}

export interface MeetingFormSend
  extends Omit<MeetingForm, "type" | "calendarType" | "company" | "title"> {
  type: MeetingType;
  calendarType?: CalendarType;
  statusId?: string;
  companyCourseIds?: string[];
  bookingProtocol?: 2;
  proposalToken?: string;
  csmOwnerId?: string;
}

/** Corps de POST /meetings : rendez-vous sans entreprise (spec 2026-10-08). */
export interface StandaloneMeetingFormSend {
  startAt: string;
  title: string;
  type: MeetingType;
  calendarType: CalendarType;
  csmOwnerId?: string;
}

/** Filtres de la liste des rendez-vous d'une fiche entreprise : la fiche a un
 *  onglet par `calendarType`, chacun ne demande que les siens. */
export interface CompanyMeetingFilters extends IFilters {
  calendarType?: CalendarType;
}

export interface MeetingFilters extends IFilters {
  date: string;
  aes?: MultiValue<User>;
  sdrs?: MultiValue<User>;
  aeIds?: string[];
  sdrIds?: string[];
  status?: MultiValue<CompanyStatus>;
  statusIds?: string[];
  companyCourses?: MultiValue<CompanyCourse>;
  companyCourseIds?: string[];
  unknownStatus?: boolean;
  activitySectors?: MultiValue<ActivitySector>;
  activitySectorIds?: string[];
  opcos?: MultiValue<Opco>;
  opcoIds?: string[];
  reminderDatetime?: string | null;
  siret?: string | null;
  city?: string | null;
  zipCode?: string | null;
  name?: string | null;
  phone?: string | null;
}

export default Meeting;
